import {
    ApiWrapper,
    FileCropConfig,
    UploadConfig,
    jObjectLittle,
    jFileLittle,
    jFileLittleCrop,
    UserDownload,
    FormatList,
    FileCopyParams,
} from '../../types';
import { OF } from '../../util/network';
import { JBaseApi } from '../base';

export class JFileApi extends JBaseApi {
    /**
     * Upload a file to the server. use the same token to link the file to an article
     * @param config configuration object. One of "file" and "url" is mandatory
     */
    public upload(config: UploadConfig) {
        const body = new FormData();
        body.append('attrName', config.attrName);
        if (config.file) {
            body.append(config.attrName, config.file);
        } else if (config.url) {
            body.append('url', config.url);
        }
        body.append('token', config.token);
        return this.network.postFile(body);
    }

    /**
     * Upload a list of files to the server. use the same token to link the file to an article
     * @param configArray
     */
    public async uploadMany(configArray: Array<UploadConfig>) {
        return Promise.all(configArray.map((config) => this.upload(config)));
    }

    public async get<T extends jFileLittle>(
        idFile: number,
        format?: FormatList,
        options?: {
            signal?: AbortSignal;
        },
    ): Promise<ApiWrapper<T>> {
        return this.network.post(
            {
                o: 'file',
                f: 'get',
                idFile,
                format,
            } as OF,
            options?.signal,
        );
    }

    public async getParent<T extends jObjectLittle>(
        idFile: number,
        format?: FormatList,
        hash?: string,
        options?: {
            signal?: AbortSignal;
        },
    ): Promise<ApiWrapper<T>> {
        return this.network.post(
            {
                o: 'file',
                f: 'getParent',
                idFile,
                format,
                hash,
            } as OF,
            options?.signal,
        );
    }

    public async getWithHash<T extends jFileLittle>(
        idFile: number,
        hash: string,
        format?: FormatList,
        options?: { signal?: AbortSignal },
    ): Promise<ApiWrapper<T>> {
        return this.network.post(
            {
                o: 'file',
                f: 'getWithHash',
                idFile,
                hash,
                format,
            } as OF,
            options?.signal,
        );
    }

    public async duplicate(
        idFile: number,
        title: string,
        publishTo?: string[],
    ): Promise<ApiWrapper<{ idFile: number; article: jObjectLittle }>> {
        return this.network.postCSRF({
            o: 'file',
            f: 'duplicateFile',
            idFile,
            title,
            publishTo,
        } as OF);
    }

    public async updateFile(idFile: number, title: string): Promise<ApiWrapper<boolean>> {
        return this.network.postCSRF({
            o: 'file',
            f: 'updateFile',
            idFile,
            title,
        } as OF);
    }

    /**
     * Crop an image
     * @param idFile
     * @param type
     * @param config crop configuration
     * @param options
     * @returns
     */
    public async update(
        idFile: number | string,
        type: string,
        config: FileCropConfig,
        options?: {
            signal?: AbortSignal;
        },
    ) {
        return this.network.post<jFileLittleCrop>(
            {
                o: 'file',
                f: 'update',
                idFile,
                type,
                cropDatas: {
                    ...config,
                    rotateBeforeCrop: true,
                },
                killSize: config.killSize,
                format: 'raw-little',
            } as OF,
            options?.signal,
        );
    }

    /**
     * delete file. This action is not automatic. You need to update the content associated with the deleted image with the same token
     * @param idFile file id
     * @param attrName attribute name
     * @param token token
     * @param signal
     * @returns
     */
    public async deleteFile(idFile: number | string, attrName: string, token: string, signal?: AbortSignal) {
        return this.network.post<boolean>(
            {
                o: 'file',
                f: 'delete',
                idFile,
                attrName,
                token,
            } as OF,
            signal,
        );
    }

    /**
     * delete the image associated with a content. This action is permanent. There is no two step process with a token
     * @param id content id
     * @param type content type
     * @param options
     * @returns
     */
    public async deleteImage(id: number | string, type: string, options?: { signal?: AbortSignal }) {
        return this.network.post<{ id: number }>(
            {
                o: 'file',
                f: 'deleteImage',
                id,
                type,
            } as OF,
            options?.signal,
        );
    }

    public getDownload(
        id: number,
        options?: { signal?: AbortSignal },
    ): Promise<
        ApiWrapper<{
            count: number;
            list: UserDownload[];
        }>
    > {
        return this.network.post(
            {
                o: 'file',
                f: 'getDownload',
                id,
            } as OF,
            options?.signal,
        );
    }

    public generatePreview(id: number, options?: { signal?: AbortSignal }): Promise<ApiWrapper<boolean>> {
        return this.network.post(
            {
                o: 'file',
                f: 'generatePreview',
                id,
            } as OF,
            options?.signal,
        );
    }

    public copy<T extends jFileLittle = jFileLittle>(
        params: FileCopyParams,
        options?: { format?: FormatList; signal?: AbortSignal },
    ): Promise<ApiWrapper<T>> {
        return this.network.post(
            {
                o: 'file',
                f: 'copy',
                ...params,
                format: options?.format ?? 'raw-little',
            } as OF,
            options?.signal,
        );
    }
}
